import React, { useEffect, useState } from "react";
import {
  ChevronLeft,
  CreditCard,
  ChevronUp,
  ChevronDown,
  AlertCircle,
  Check,
  Wallet,
  Ticket,
  X,
  ArrowRight,
  Film,
  Clock,
  Tag,
  MapPin,
  Shield,
  Coffee,
  Info,
  Search,
  Plus,
  Minus,
  ShoppingCart,
  Filter,
  MessageCircle,
} from "lucide-react";
import axios from "axios";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

const GST_PERCENTAGE = 0;

// ---------- UI helpers ----------
const getTypeBorderColor = (type) =>
  ({
    VIP: "border-red-600",
    Premium: "border-amber-500",
    Recliner: "border-emerald-600",
  }[type] || "border-gray-300");

const getTypeColor = (type) =>
  ({
    VIP: "bg-red-600",
    Premium: "bg-amber-500",
    Recliner: "bg-emerald-600",
  }[type] || "bg-gray-300");

const getTypeGradient = (type) =>
  ({
    VIP: "bg-gradient-to-r from-red-600 to-red-500",
    Premium: "bg-gradient-to-r from-amber-500 to-amber-400",
    Recliner: "bg-gradient-to-r from-emerald-600 to-emerald-500",
  }[type] || "bg-gradient-to-r from-gray-400 to-gray-300");

// ---------- HEADER ----------
const Header = ({ showDetails, navigateBack }) => (
  <div className="bg-white border-b border-gray-200 shadow-sm p-4 md:p-6 sticky top-0 z-20">
    <div className="max-w-7xl mx-auto flex items-center mb-3">
      <button
        onClick={navigateBack}
        className="mr-3 p-2 hover:bg-gray-100 rounded-full transition-colors"
      >
        <ChevronLeft size={24} className="text-gray-700" />
      </button>
      <h1 className="text-xl md:text-2xl font-bold text-gray-900">
        Select Your Perfect Seats
      </h1>
    </div>
    {showDetails && (
      <div className="max-w-7xl mx-auto mt-2 flex flex-col md:flex-row justify-between items-start md:items-center">
        <div className="flex items-center">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-gray-100 flex items-center justify-center mr-3">
            <Film size={24} className="text-red-600" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-bold text-gray-900">
              {showDetails.movieId?.movieName}
            </h2>
            <div className="flex items-center text-sm text-gray-600">
              <Clock size={14} className="mr-1 text-red-600" />
              {new Date(showDetails.showTime).toLocaleString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
                hour12: true,
              })}
            </div>
          </div>
        </div>
        <div className="mt-3 md:mt-0 flex items-center bg-red-50 px-3 py-1.5 rounded-full">
          <MapPin size={14} className="mr-1 text-red-600" />
          <span className="text-sm font-medium text-red-800">
            {showDetails.audiId?.audiName}
          </span>
        </div>
      </div>
    )}
  </div>
);

const SeatLegend = ({ showDetails }) => {
  const getPriceByType = (type) => {
    if (!showDetails || !showDetails.categoryPrices) return "";
    const priceInfo = showDetails.categoryPrices.find((p) => p.type === type);
    return priceInfo ? `₹${priceInfo.price}` : "";
  };
  const types = [
    { name: "Normal", color: "bg-gray-300" },
    { name: "Premium", color: "bg-amber-500" },
    { name: "VIP", color: "bg-red-600" },
    { name: "Recliner", color: "bg-emerald-600" },
  ];
  return (
    <div className="flex flex-wrap justify-center gap-3 md:gap-5 py-4 text-xs md:text-sm">
      {types.map(({ name, color }) => (
        <div
          key={name}
          className="flex items-center px-3 py-1.5 rounded-full bg-white border border-gray-200 shadow-sm"
        >
          <div
            className={`w-4 h-4 md:w-5 md:h-5 ${color} rounded-md mr-2`}
          ></div>
          <span className="text-gray-800">
            {name}{" "}
            {getPriceByType(name) && (
              <span className="font-medium text-red-600">
                ({getPriceByType(name)})
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
};

const Screen = () => (
  <div className="mt-8 mb-10 text-center">
    <div className="h-2 md:h-3 mx-auto w-4/5 lg:w-3/5 bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300 rounded-lg shadow-lg"></div>
    <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-50 w-11/12 md:w-4/5 lg:w-2/3 mx-auto h-16 md:h-20 mt-1 perspective transform-gpu rotate-x-12 rounded-b-[50%] border-t border-gray-300"></div>
    <p className="text-xs md:text-sm text-gray-500 mt-4 font-medium">SCREEN</p>
  </div>
);

// ----------- FoodSelection (child) ------------
const FoodSelection = ({
  foodCart,
  setFoodCart,
  categories,
  foodItems,
  loading,
  error,
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  showCartPreview,
  setShowCartPreview,
}) => {
  const addItemToCart = (item) => {
    setFoodCart((prev) => {
      const found = prev.find((cartItem) => cartItem._id === item._id);
      if (found) {
        return prev.map((cartItem) =>
          cartItem._id === item._id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  };
  const removeItemFromCart = (itemId) => {
    setFoodCart((prev) => {
      const found = prev.find((item) => item._id === itemId);
      if (found && found.quantity > 1) {
        return prev.map((item) =>
          item._id === itemId ? { ...item, quantity: item.quantity - 1 } : item
        );
      } else {
        return prev.filter((item) => item._id !== itemId);
      }
    });
  };
  const getTotalCartPrice = () =>
    foodCart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const filteredItems = foodItems
    .filter(
      (item) =>
        (item.name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (item.description?.toLowerCase() || "").includes(
          searchTerm.toLowerCase()
        )
    )
    .filter(
      (item) =>
        selectedCategory === "all" || item?.category?.name === selectedCategory
    );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Loading menu items...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-red-50 border border-red-200 rounded-lg p-6">
        <X className="text-red-500 w-12 h-12 mb-4" />
        <p className="text-red-600 font-medium">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }
  return (
    <div className="relative">
      <div className="flex flex-col md:flex-row gap-4 sticky top-0 bg-white z-10 p-4 border-b border-gray-200 shadow-sm">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search for snacks, beverages..."
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="relative">
          <select
            className="appearance-none bg-white border border-gray-300 px-4 py-2 pr-8 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            {categories.map((cat, idx) => (
              <option key={idx} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <Filter className="h-4 w-4 text-gray-400" />
          </div>
        </div>
        <button
          className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 relative"
          onClick={() => setShowCartPreview(!showCartPreview)}
        >
          <ShoppingCart className="h-5 w-5 mr-2" />
          <span>Cart</span>
          {foodCart.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-white text-red-600 w-6 h-6 flex items-center justify-center rounded-full border-2 border-red-600 text-xs font-bold">
              {foodCart.reduce((sum, item) => sum + item.quantity, 0)}
            </span>
          )}
        </button>
      </div>
      {showCartPreview && (
        <div className="absolute right-4 mt-2 z-20 w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-800">Your Cart</h3>
            <button
              onClick={() => setShowCartPreview(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {foodCart.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Your cart is empty</p>
          ) : (
            <>
              <div className="max-h-60 overflow-y-auto mb-4">
                {foodCart.map((item) => (
                  <div
                    key={item._id}
                    className="flex items-center justify-between py-2 border-b border-gray-100"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{item.name}</p>
                      <p className="text-gray-500 text-sm">
                        ₹{item.price} × {item.quantity}
                      </p>
                    </div>
                    <div className="flex items-center">
                      <button
                        onClick={() => removeItemFromCart(item._id)}
                        className="p-1 text-gray-500 hover:text-red-600"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <button
                        onClick={() => addItemToCart(item)}
                        className="p-1 text-gray-500 hover:text-green-600"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-200 pt-3 pb-2">
                <div className="flex justify-between font-bold text-gray-800 mb-4">
                  <span>Total:</span>
                  <span>₹{getTotalCartPrice().toFixed(2)}</span>
                </div>
                <button
                  onClick={() => setShowCartPreview(false)}
                  className="w-full py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      )}
      <div className="p-4">
        {filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <img
              src="/api/placeholder/120/120"
              alt="No results found"
              className="mx-auto mb-4 opacity-50"
            />
            <p className="text-gray-600 font-medium">No items found</p>
            <p className="text-gray-500 text-sm mt-1">
              Try changing your search or filter
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredItems.map((item) => (
              <div
                key={item._id}
                className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white"
              >
                <div className="relative h-48 overflow-hidden bg-gray-100">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <Coffee className="h-12 w-12 text-gray-400" />
                    </div>
                  )}
                  {item.category?.name && (
                    <div className="absolute top-2 left-2 px-2 py-1 bg-red-600 text-white text-xs font-medium rounded-full">
                      {item.category.name}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-800 text-lg mb-1">
                    {item.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {item.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-red-600">
                      ₹{item.price}
                    </span>
                    <div className="flex items-center">
                      {foodCart.find(
                        (cartItem) => cartItem._id === item._id
                      ) ? (
                        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                          <button
                            onClick={() => removeItemFromCart(item._id)}
                            className="p-1 bg-gray-100 hover:bg-gray-200 transition-colors"
                          >
                            <Minus className="h-4 w-4 text-gray-600" />
                          </button>
                          <span className="px-3 py-1 text-gray-800">
                            {
                              foodCart.find(
                                (cartItem) => cartItem._id === item._id
                              )?.quantity
                            }
                          </span>
                          <button
                            onClick={() => addItemToCart(item)}
                            className="p-1 bg-gray-100 hover:bg-gray-200 transition-colors"
                          >
                            <Plus className="h-4 w-4 text-gray-600" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => addItemToCart(item)}
                          className="flex items-center px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* {foodCart.length > 0 && !showCartPreview && (
        <div className="fixed bottom-16 inset-x-0 bg-white border-t border-gray-200 shadow-lg p-4 flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-800">
              {foodCart.reduce((sum, item) => sum + item.quantity, 0)} {foodCart.reduce((sum, item) => sum + item.quantity, 0) === 1 ? 'item' : 'items'}
            </p>
            <p className="font-bold text-red-600">₹{getTotalCartPrice().toFixed(2)}</p>
          </div>
          <button onClick={() => setShowCartPreview(true)}
            className="px-6 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors">
            View Cart
          </button>
        </div>
      )} */}
    </div>
  );
};

// --------------- MAIN COMPONENT ---------------
const SeatSelection = () => {
  const { showId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const showDetails = location.state?.showDetails;
  // SEAT & FOOD SELECTION STATE
  const [audiRows, setAudiRows] = useState([]);
  const [bookedSeats, setBookedSeats] = useState([]);
  const [selectedSeats, setSelectedSeats] = useState([]);
  // FOOD
  const [foodCart, setFoodCart] = useState([]);
  const [foodLoading, setFoodLoading] = useState(true);
  const [foodError, setFoodError] = useState(null);
  const [availableFoodItems, setAvailableFoodItems] = useState([]);
  const [foodCategories, setFoodCategories] = useState([]);
  const [foodSearchTerm, setFoodSearchTerm] = useState("");
  const [foodCategory, setFoodCategory] = useState("all");
  const [showCartPreview, setShowCartPreview] = useState(false);
  // TABS & MODAL
  const [activeTab, setActiveTab] = useState("seats");
  const [showSeatCountModal, setShowSeatCountModal] = useState(true);
  const [seatCountToSelect, setSeatCountToSelect] = useState(1);
  const [seatAutoSelectionMode, setSeatAutoSelectionMode] = useState(false);
  const [lastAutoSelectedRow, setLastAutoSelectedRow] = useState(null);
  // PAYMENT
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("razorpay");
  // VOUCHER, WALLET, MESSAGE
  const [voucherCode, setVoucherCode] = useState("");
  const [appliedVoucher, setAppliedVoucher] = useState(null);
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherError, setVoucherError] = useState("");
  const [voucherDiscount, setVoucherDiscount] = useState(0);
  const [showVoucherSection, setShowVoucherSection] = useState(false);
  const [availableVouchers, setAvailableVouchers] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerWalletPoints, setCustomerWalletPoints] = useState(0);
  const [useWalletPoints, setUseWalletPoints] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const token = localStorage.getItem("token");
  const [decodedUser, setDecodedUser] = useState(null);

  // --------------- EFFECTS ---------------
  useEffect(() => {
    if (!token) return navigate("/login", { replace: true });
    try {
      setDecodedUser(jwtDecode(token));
    } catch (err) {
      navigate("/login", { replace: true });
    }
  }, [navigate, token]);
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (userData) {
      setCustomerName(userData.name);
      setCustomerPhone(userData.phone);
      if (userData.walletPoints) setCustomerWalletPoints(userData.walletPoints);
    }
  }, []);
  useEffect(() => {
    if (showDetails && showDetails.audiId) {
      fetchAudiLayout(showDetails.audiId._id);
      fetchBookedSeats();
    }
  }, [showDetails]);
  // Wallet fetch
  useEffect(() => {
    if (!token) return;
    axios
      .get(
        `https://apigrand.dodunsoftsolutions.com/api/user/userbyid/${decodedUser?.userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        if (res.data && typeof res.data.walletPoints !== "undefined") {
          setCustomerWalletPoints(res.data.walletPoints);
        }
      })
      .catch(() => {});
  }, [token, decodedUser?.userId]);
  useEffect(() => {
    if (token && showVoucherSection) fetchAvailableVouchers();
  }, [token, showVoucherSection]);
  // Food menu fetch
  useEffect(() => {
    const fetchFoodItems = async () => {
      try {
        setFoodLoading(true);
        const response = await fetch(
          "https://apigrand.dodunsoftsolutions.com/api/kitchen/items"
        );
        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        setAvailableFoodItems(Array.isArray(data) ? data : []);
        setFoodCategories([
          ...new Set(data.map((item) => item?.category?.name || "Other")),
        ]);
        setFoodLoading(false);
      } catch (err) {
        setFoodError("Failed to load food menu. Please try again.");
        setFoodLoading(false);
      }
    };
    fetchFoodItems();
  }, []);

  // ----------------- FETCH METHODS ---------------------
  const fetchAudiLayout = async (audiId) => {
    try {
      const response = await axios.get(
        `https://apigrand.dodunsoftsolutions.com/api/audi/${audiId}`
      );
      setAudiRows(response.data.rowsData);
    } catch {
      setMessage("Failed to load seat layout");
      setMessageType("error");
    }
  };
  const fetchBookedSeats = async () => {
    try {
      const response = await axios.get(
        `https://apigrand.dodunsoftsolutions.com/api/booking/show/${showId}`
      );
      const booked = response.data.flatMap((b) =>
        b.bookedSeats.map((seat) => `${seat.row}${seat.seatNumber}`)
      );
      setBookedSeats(booked);
    } catch {
      setMessage("Failed to load booked seats");
      setMessageType("error");
    }
  };
  const fetchAvailableVouchers = async () => {
    try {
      const res = await fetch(
        "https://apigrand.dodunsoftsolutions.com/api/voucher/all",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const json = await res.json();
      setAvailableVouchers(
        (json.vouchers || []).filter(
          (v) => v.isActive && new Date(v.expiryDate) > new Date()
        )
      );
    } catch {}
  };

  // ----------------- SEAT HANDLERS ---------------------
  const handleSeatClick = (seatId, seatType) => {
    if (bookedSeats.includes(seatId)) return;
    const clickedRow = seatId[0];
    const clickedSeatNumber = parseInt(seatId.slice(1));
    const isAlreadySelected = selectedSeats.find((s) => s.seatId === seatId);

    // Deselect
    if (isAlreadySelected) {
      setSelectedSeats(selectedSeats.filter((s) => s.seatId !== seatId));
      return;
    }

    // Auto-selection
    const seatsInRow = audiRows.find((r) => r.row === clickedRow);
    if (!seatsInRow) return;
    const shouldAutoSelect =
      seatAutoSelectionMode ||
      lastAutoSelectedRow === null ||
      (lastAutoSelectedRow && clickedRow !== lastAutoSelectedRow);
    if (shouldAutoSelect) {
      const potentialSeats = [];
      for (let i = 0; i < seatCountToSelect; i++) {
        const currentSeatId = `${clickedRow}${clickedSeatNumber + i}`;
        if (
          clickedSeatNumber + i <= seatsInRow.seats &&
          !bookedSeats.includes(currentSeatId) &&
          !selectedSeats.find((s) => s.seatId === currentSeatId)
        ) {
          potentialSeats.push({ seatId: currentSeatId, seatType });
        } else break;
      }
      if (potentialSeats.length === seatCountToSelect) {
        setSelectedSeats(potentialSeats);
        setLastAutoSelectedRow(clickedRow);
        setSeatAutoSelectionMode(false);
      } else {
        setMessage(
          `Only ${potentialSeats.length} adjacent seats available in Row ${clickedRow}. Try starting from an earlier seat.`
        );
        setMessageType("error");
        setTimeout(() => setMessage(""), 4000);
      }
      return;
    }

    setSelectedSeats((prev) => [...prev, { seatId, seatType }]);
  };

  // ----------------- PAYMENT CALCS ---------------------
  const getSeatPrice = (type) =>
    showDetails?.categoryPrices?.find((p) => p.type === type)?.price || 0;

  const calculateBaseAmount = () => {
    const totalPrice = selectedSeats.reduce(
      (sum, seat) => sum + getSeatPrice(seat.seatType),
      0
    );
    return totalPrice.toFixed(2);
  };

  const gstAmount = () =>
    ((calculateBaseAmount() * GST_PERCENTAGE) / 100).toFixed(2);

  const subTotal = () =>
    (parseFloat(calculateBaseAmount()) + parseFloat(gstAmount())).toFixed(2);

  const getTotalFoodAmount = () =>
    foodCart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const calculateWalletDiscount = () => {
    if (!useWalletPoints || customerWalletPoints <= 0) return 0;
    const subtotalValue =
      parseFloat(subTotal()) + parseFloat(getTotalFoodAmount());
    return Math.min(customerWalletPoints, subtotalValue);
  };

  const finalAmount = () => {
    const subtotalValue =
      parseFloat(subTotal()) + parseFloat(getTotalFoodAmount());
    const totalDiscount =
      parseFloat(voucherDiscount) + calculateWalletDiscount();
    return Math.max(subtotalValue - totalDiscount, 0).toFixed(2);
  };

  // ----------------- ORDER + PAYMENT ---------------------
  const applyVoucher = async () => {
    if (!voucherCode.trim()) {
      setVoucherError("Please enter a voucher code");
      return;
    }
    setVoucherLoading(true);
    setVoucherError("");
    try {
      const res = await fetch(
        "https://apigrand.dodunsoftsolutions.com/api/voucher/apply",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            code: voucherCode,

            orderAmount: parseFloat(finalAmount()),
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Error applying voucher");
      setAppliedVoucher({
        code: data.appliedVoucher.code,
        discount: data.discount,
      });
      setVoucherDiscount(data.discount);
      setVoucherCode("");
      setMessage(`Applied successfully! You saved ₹${data.discount}`);
      setMessageType("success");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setVoucherError(err.message);
      setVoucherDiscount(0);
      setAppliedVoucher(null);
    } finally {
      setVoucherLoading(false);
    }
  };

  const removeVoucher = () => {
    setAppliedVoucher(null);
    setVoucherDiscount(0);
    setVoucherError("");
  };

  const handleSubmit = () => {
    if (!token) {
      setMessageType("error");
      setMessage("Please login to book seats");
      setTimeout(() => navigate("/login"), 1500);
      return;
    }
    if (!customerName || !customerPhone) {
      setMessageType("error");
      setMessage("Customer details missing. Please login again");
      setTimeout(() => navigate("/login"), 1500);
      return;
    }
    if (selectedSeats.length === 0) {
      setMessageType("error");
      setMessage("Please select at least one seat");
      setTimeout(() => setMessage(""), 3000);
      return;
    }
    setShowSeatCountModal(false);
    completeBooking();
  };

const completeBooking = async () => {
  setLoading(true);
  
  try {
    const walletPointsUsed = useWalletPoints ? calculateWalletDiscount() : 0;
    
    // Fetch show details
    let showDetails = null;
    try {
      const showResponse = await axios.get(
        `https://apigrand.dodunsoftsolutions.com/api/show/getById/${showId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showDetails = showResponse.data;
    } catch (error) {
      console.error("Error fetching show details:", error);
      // Continue without show details if fetch fails
    }
    
    // Prepare booking details
    const bookingDetails = {
      showId,
      showDetails,
      user: decodedUser.userId,
      bookedSeats: selectedSeats.map((s) => ({
        row: s.seatId[0],
        seatNumber: +s.seatId.slice(1),
        type: s.seatType,
      })),
      pricePerSeat: +getSeatPrice("Normal"),
      customerName,
      customerPhone,
      paymentMethod,
      voucherDiscount,
      walletPointsUsed,
      finalAmount: +finalAmount(),
      voucherCode: appliedVoucher?.code || null,
      orderedFoodItems: foodCart,
    };

    // Format message for WhatsApp
    const formatWhatsAppMessage = () => {
      const seatsList = bookingDetails.bookedSeats
        .map(seat => `${seat.row}${seat.seatNumber} (${seat.type})`)
        .join(', ');
      
      const foodItems = bookingDetails.orderedFoodItems.length > 0
        ? bookingDetails.orderedFoodItems
            .map(item => `${item.name} x${item.quantity} - ₹${item.price * item.quantity}`)
            .join('\n')
        : 'No food items';

      // Helper function to format time
      const formatShowTime = (dateString) => {
        if (!dateString) return 'Not specified';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
          weekday: 'long',
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }) + ' at ' + date.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      };

      let message = `🎬 *MOVIE BOOKING REQUEST*\n\n`;
      
      // Show Details Section
      if (bookingDetails.showDetails) {
        const show = bookingDetails.showDetails;
        message += `🎭 *Show Details:*\n`;
        message += `Movie: ${show.movieId?.movieName || 'Not specified'}\n`;
        message += `Audi: ${show.audiId?.audiName || 'Not specified'}\n`;
        message += `Show Time: ${formatShowTime(show.showTime)}\n\n`;
      } else {
        message += `🎭 *Show Details:*\n`;
        message += `Show ID: ${bookingDetails.showId}\n\n`;
      }
      
      message += `👤 *Customer Details:*\n`;
      message += `Name: ${bookingDetails.customerName}\n`;
      message += `Phone: ${bookingDetails.customerPhone}\n`;
      message += `Email: ${decodedUser.email}\n\n`;
      
      message += `🎫 *Booking Details:*\n`;
      message += `Seats: ${seatsList}\n`;
      message += `Price per seat: ₹${bookingDetails.pricePerSeat}\n\n`;
      
      message += `🍿 *Food Items:*\n${foodItems}\n\n`;
      
     
      
      if (bookingDetails.voucherCode) {
        message += `Voucher Code: ${bookingDetails.voucherCode}\n`;
        message += `Voucher Discount: ₹${bookingDetails.voucherDiscount}\n`;
      }
      
      if (bookingDetails.walletPointsUsed > 0) {
        message += `Wallet Points Used: ₹${bookingDetails.walletPointsUsed}\n`;
      }
      
      message += `*Final Amount: ₹${bookingDetails.finalAmount}*\n\n`;
      message += `Please confirm this booking and provide payment instructions. Thank you! 🙏`;
      
      return encodeURIComponent(message);
    };

    // WhatsApp business number (replace with actual number)
    const whatsappNumber = "916230301476"; // Replace with your WhatsApp business number
    const message = formatWhatsAppMessage();
    
    // Function to detect mobile device
    const isMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    // Function to open WhatsApp
    const openWhatsApp = () => {
      if (isMobile()) {
        // For mobile devices, try multiple approaches
        const whatsappAppURL = `whatsapp://send?phone=${whatsappNumber}&text=${message}`;
        const whatsappWebURL = `https://wa.me/${whatsappNumber}?text=${message}`;
        const whatsappAPIURL = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${message}`;
        
        // Try to open WhatsApp app first
        const link = document.createElement('a');
        link.href = whatsappAppURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Fallback to web version after a short delay
        setTimeout(() => {
          window.open(whatsappWebURL, '_blank');
        }, 1000);
        
        // Show instructions to user
        setMessage("If WhatsApp didn't open automatically, please copy the booking details and send to our WhatsApp number: " + whatsappNumber.replace('91', '+91 '));
        setMessageType("info");
        
      } else {
        // For desktop, use wa.me
        const whatsappURL = `https://wa.me/${whatsappNumber}?text=${message}`;
        window.open(whatsappURL, '_blank');
      }
    };

    // Open WhatsApp
    openWhatsApp();
    
    setMessage("Booking details sent to WhatsApp! Please complete the booking process there.");
    setMessageType("success");
    
    // Reset form
    setSelectedSeats([]);
    setAppliedVoucher(null);
    setVoucherDiscount(0);
    setUseWalletPoints(false);
    setFoodCart([]);
    fetchBookedSeats();
    
    // Optional: Navigate after a delay to give time for WhatsApp to open
    setTimeout(() => {
      navigate("/bookings");
    }, 1500);
    
    setLoading(false);
    
  } catch (err) {
    console.error("Booking error:", err);
    setMessageType("error");
    setMessage("Failed to send booking details. Please try again.");
    setLoading(false);
  }
};

  // ---------- RENDER ----------
  const getSeatCountByType = () => {
    const counts = selectedSeats.reduce((acc, seat) => {
      acc[seat.seatType] = (acc[seat.seatType] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).map(([type, count]) => {
      const price = getSeatPrice(type);
      return { type, count, price };
    });
  };

  const seatCounts = getSeatCountByType();

  return (
    <div className="max-w-full mx-auto bg-white min-h-screen flex flex-col">
      {showSeatCountModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 w-80 shadow-lg">
            <h2 className="text-lg font-bold text-gray-800 mb-4 text-center">
              Select Number of Seats
            </h2>
            <input
              type="number"
              min={1}
              max={10}
              value={seatCountToSelect}
              onChange={(e) =>
                setSeatCountToSelect(
                  Math.max(1, parseInt(e.target.value, 10) || 1)
                )
              }
              className="w-full border border-gray-300 px-4 py-2 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <button
              onClick={() => {
                if (seatCountToSelect > 0) {
                  setSeatAutoSelectionMode(true);
                  setShowSeatCountModal(false);
                }
              }}
              className="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-medium"
            >
              Confirm
            </button>
          </div>
        </div>
      )}
      <Header showDetails={showDetails} navigateBack={() => navigate(-1)} />
      {message && (
        <div
          className={`mx-auto max-w-2xl my-4 p-4 rounded-lg flex items-center shadow-md ${
            messageType === "success"
              ? "bg-green-50 border border-green-200"
              : "bg-red-50 border border-red-200"
          }`}
        >
          {messageType === "success" ? (
            <Check size={20} className="text-green-600 mr-3" />
          ) : (
            <AlertCircle size={20} className="text-red-600 mr-3" />
          )}
          <p
            className={`${
              messageType === "success" ? "text-green-700" : "text-red-700"
            } font-medium`}
          >
            {message}
          </p>
        </div>
      )}
      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {["seats", "food", "payment"].map((tab) => (
          <button
            key={tab}
            className={`py-3 px-6 font-medium text-sm transition-colors duration-200 relative ${
              activeTab === tab
                ? "text-red-600"
                : "text-gray-500 hover:text-gray-700"
            } ${
              tab === "payment" && selectedSeats.length === 0
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
            onClick={() =>
              tab === "payment"
                ? selectedSeats.length > 0 && setActiveTab(tab)
                : setActiveTab(tab)
            }
            disabled={tab === "payment" && selectedSeats.length === 0}
          >
            {tab === "seats"
              ? "Select Seats"
              : tab === "food"
              ? "Select Food"
              : "Payment & Checkout"}
            {activeTab === tab && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-600"></span>
            )}
          </button>
        ))}
      </div>
      {/* SEATS TAB */}
      {activeTab === "seats" && (
        <div className="flex-1 overflow-auto px-4 md:px-8 lg:px-12 max-w-6xl mx-auto w-full">
          <div className="flex items-center justify-between mb-4">
            <SeatLegend showDetails={showDetails} />
          </div>
          <Screen />
          <div className="space-y-1 md:space-y-2 max-w-4xl mx-auto mb-12">
            {audiRows.map((row) => (
              <div key={row.row} className="flex items-center mb-1 md:mb-2">
                <span className="w-6 md:w-8 text-center font-bold text-gray-600">
                  {row.row}
                </span>
                <div className="flex flex-1 justify-center gap-1 md:gap-2">
                  {Array.from({ length: row.seats }).map((_, seatIndex) => {
                    const seatId = `${row.row}${seatIndex + 1}`;
                    const isBooked = bookedSeats.includes(seatId);
                    const isSelected = selectedSeats.some(
                      (s) => s.seatId === seatId
                    );
                    const price = getSeatPrice(row.type);
                    return (
                      <div
                        key={seatId}
                        className={`w-7 h-7 md:w-9 md:h-9 flex items-center justify-center rounded-md text-xs md:text-sm relative
                        ${
                          isBooked
                            ? "bg-gray-300 opacity-40 cursor-not-allowed"
                            : isSelected
                            ? `${getTypeGradient(
                                row.type
                              )} text-white shadow-xl scale-110 transform-gpu`
                            : `border-2 ${getTypeBorderColor(
                                row.type
                              )} bg-transparent hover:bg-opacity-20 hover:${getTypeColor(
                                row.type
                              )} transition-all duration-200`
                        } cursor-pointer`}
                        onClick={() => handleSeatClick(seatId, row.type)}
                        title={`${row.type} - ₹${price}`}
                      >
                        {seatIndex + 1}
                        {isSelected && (
                          <div className="absolute -top-1 -right-1 w-2 h-2 bg-white rounded-full shadow-sm"></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* FOOD TAB */}
      {activeTab === "food" && (
        <div className="flex-1 overflow-auto px-4 md:px-8 lg:px-12 max-w-6xl mx-auto w-full">
          <FoodSelection
            foodCart={foodCart}
            setFoodCart={setFoodCart}
            categories={foodCategories}
            foodItems={availableFoodItems}
            loading={foodLoading}
            error={foodError}
            searchTerm={foodSearchTerm}
            setSearchTerm={setFoodSearchTerm}
            selectedCategory={foodCategory}
            setSelectedCategory={setFoodCategory}
            showCartPreview={showCartPreview}
            setShowCartPreview={setShowCartPreview}
          />
        </div>
      )}
      {/* PAYMENT TAB */}
      {activeTab === "payment" && (
        <div className="flex-1 overflow-auto px-4 md:px-8 lg:px-12 max-w-4xl mx-auto w-full">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
              <Ticket className="mr-2 text-red-600" size={20} /> Ticket Summary
            </h3>
            {selectedSeats.length > 0 ? (
              <div>
                <div className="flex flex-col md:flex-row justify-between mb-4 pb-4 border-b border-gray-100">
                  <div>
                    <p className="text-gray-700 font-medium mb-1">
                      Selected Seats
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {selectedSeats.map((seat) => (
                        <span
                          key={seat.seatId}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {seat.seatId}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="mt-3 md:mt-0">
                    <p className="text-gray-700 font-medium mb-1">Seat Types</p>
                    <div className="space-y-1">
                      {seatCounts.map(({ type, count, price }) => (
                        <div key={type} className="flex items-center text-sm">
                          <div
                            className={`w-3 h-3 rounded-sm mr-1.5 ${getTypeColor(
                              type
                            )}`}
                          ></div>
                          <span className="text-gray-800">
                            {count}x {type}
                          </span>
                          <span className="text-gray-500 ml-1.5">
                            ₹{price} each
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Base Price:</span>
                    <span className="text-gray-900">
                      ₹{calculateBaseAmount()}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm font-medium">
                    <span className="text-gray-800">Subtotal:</span>
                    <span className="text-gray-900">₹{subTotal()}</span>
                  </div>
                  {foodCart.length > 0 && (
                    <div className="mt-4">
                      <p className="text-gray-700 font-medium mb-2">
                        Food Items Ordered:
                      </p>
                      <ul className="space-y-1 text-sm text-gray-800">
                        {foodCart.map((item) => (
                          <li key={item._id}>
                            {item.name} × {item.quantity} — ₹
                            {item.price * item.quantity}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {voucherDiscount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Voucher Discount:</span>
                      <span>- ₹{voucherDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  {useWalletPoints && calculateWalletDiscount() > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Wallet Points:</span>
                      <span>- ₹{calculateWalletDiscount().toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-200 pt-3 mt-2">
                    <div className="flex justify-between font-bold">
                      <span className="text-gray-900">Total Amount:</span>
                      <span className="text-red-600 text-lg">
                        ₹{finalAmount()}
                      </span>
                    </div>
                    {(voucherDiscount > 0 ||
                      (useWalletPoints && calculateWalletDiscount() > 0)) && (
                      <p className="text-green-600 text-xs text-right mt-1">
                        You saved ₹
                        {(
                          parseFloat(voucherDiscount) +
                          calculateWalletDiscount()
                        ).toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">
                No seats selected. Please go back and select seats.
              </p>
            )}
          </div>
          {/* Discount Section */}
          {selectedSeats.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 overflow-hidden">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800 flex items-center">
                  <Tag className="mr-2 text-red-600" size={20} /> Apply Discount
                </h3>
                <button
                  onClick={() => setShowVoucherSection(!showVoucherSection)}
                  className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center"
                >
                  {showVoucherSection ? "Hide" : "Show"} Offers{" "}
                  {showVoucherSection ? (
                    <ChevronUp size={16} className="ml-1" />
                  ) : (
                    <ChevronDown size={16} className="ml-1" />
                  )}
                </button>
              </div>
              {customerWalletPoints > 0 && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4">
                  <div className="flex items-center">
                    <Wallet size={18} className="text-gray-600 mr-2" />
                    <div>
                      <p className="font-medium text-gray-800">
                        Use Wallet Points
                      </p>
                      <p className="text-xs text-gray-600">
                        Available: ₹{customerWalletPoints.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={useWalletPoints}
                      onChange={() => setUseWalletPoints(!useWalletPoints)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                  </label>
                </div>
              )}
              {/* Voucher Section */}
              {showVoucherSection && (
                <div className="animate-fadeIn">
                  <div className="flex space-x-2 mb-4">
                    <input
                      type="text"
                      value={voucherCode}
                      onChange={(e) =>
                        setVoucherCode(e.target.value.toUpperCase())
                      }
                      placeholder="Enter voucher code"
                      disabled={voucherLoading || !!appliedVoucher}
                      className="flex-1 border border-gray-300 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white placeholder:text-gray-400"
                    />
                    {appliedVoucher ? (
                      <button
                        onClick={removeVoucher}
                        className="px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
                      >
                        Remove
                      </button>
                    ) : (
                      <button
                        onClick={applyVoucher}
                        disabled={voucherLoading || !voucherCode.trim()}
                        className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        {voucherLoading ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        ) : null}
                        {voucherLoading ? "Applying..." : "Apply"}
                      </button>
                    )}
                  </div>
                  {voucherError && (
                    <div className="p-2 mb-4 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600">
                      {voucherError}
                    </div>
                  )}
                  {appliedVoucher && (
                    <div className="p-3 mb-4 bg-green-50 border border-green-100 rounded-lg flex items-center justify-between">
                      <div className="flex items-center">
                        <Check size={16} className="text-green-600 mr-2" />
                        <span className="text-green-800 font-medium">
                          {appliedVoucher.code}
                        </span>
                      </div>
                      <span className="text-green-600 font-medium">
                        -₹{voucherDiscount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {!appliedVoucher && availableVouchers.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2 text-sm">
                        Available Vouchers
                      </h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                        {availableVouchers.map((v) => (
                          <div
                            key={v._id}
                            onClick={() => setVoucherCode(v.code)}
                            className="p-3 border border-gray-200 hover:border-red-200 rounded-lg cursor-pointer transition-colors bg-white hover:bg-red-50"
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex items-center">
                                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mr-2">
                                  <Ticket size={16} className="text-red-600" />
                                </div>
                                <div>
                                  <span className="font-medium text-gray-800">
                                    {v.code}
                                  </span>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    Min order: ₹{v.minAmount}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="font-medium text-red-600">
                                  {v.discountType === "percentage"
                                    ? `${v.discountValue}%`
                                    : `₹${v.discountValue}`}
                                </span>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  Expires{" "}
                                  {new Date(v.expiryDate).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Payment Method Selection */}
         {selectedSeats.length > 0 && (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
      <MessageCircle className="mr-2 text-green-600" size={20} /> Booking
      Method
    </h3>
    
    <div className="flex flex-col p-4 border rounded-xl border-green-500 bg-green-50 shadow-md">
      <div className="flex items-center">
        <MessageCircle
          size={20}
          className="mr-3 text-green-600"
        />
        <span className="flex-grow font-medium">Book via WhatsApp</span>
        <div className="h-5 w-5 rounded-full bg-green-600 flex items-center justify-center">
          <Check size={12} color="white" />
        </div>
      </div>
      <p className="text-xs text-gray-600 mt-2 ml-8">
        Send booking details directly to our WhatsApp for instant confirmation and payment
      </p>
    </div>

    <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200 flex items-start">
      <MessageCircle
        className="text-green-600 mr-2 flex-shrink-0 mt-0.5"
        size={18}
      />
      <div>
        <p className="text-sm text-green-800 font-medium mb-1">
          How it works:
        </p>
        <p className="text-xs text-green-700">
          1. Click "Book Now" to send your booking details via WhatsApp<br/>
          2. Our team will confirm seat availability instantly<br/>
          3. Choose your preferred payment method and complete payment<br/>
          4. Receive booking confirmation & digital tickets immediately
        </p>
      </div>
    </div>
  </div>
)}
        </div>
      )}
      {/* FOOTER */}
      <div className="bg-white border-t border-gray-200 shadow-md py-4 px-4 md:px-8 sticky bottom-0 z-10">
  <div className="max-w-4xl mx-auto flex flex-col sm:flex-row justify-between items-center">
    <div className="mb-3 sm:mb-0 text-center sm:text-left">
      {selectedSeats.length > 0 ? (
        <div>
          <div className="flex items-center">
            <span className="text-gray-600 mr-2">Total:</span>
            <span className="text-xl font-bold text-green-600">
              ₹{finalAmount()}
            </span>
          </div>
          <div className="text-xs text-gray-500">
            {selectedSeats.length}{" "}
            {selectedSeats.length === 1 ? "ticket" : "tickets"}
          </div>
        </div>
      ) : (
        <div className="text-gray-500">No seats selected</div>
      )}
    </div>
    
    {/* CONTINUE BUTTONS */}
    {activeTab === "seats" ? (
      <button
        onClick={() => selectedSeats.length > 0 && setActiveTab("food")}
        disabled={selectedSeats.length === 0}
        className={`w-full sm:w-auto py-3 px-6 rounded-lg font-medium flex items-center justify-center ${
          selectedSeats.length === 0
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow transition-all"
        }`}
      >
        Continue to Food
        <ArrowRight className="ml-2" size={18} />
      </button>
    ) : activeTab === "food" ? (
      <button
        onClick={() =>
          selectedSeats.length > 0 && setActiveTab("payment")
        }
        disabled={selectedSeats.length === 0}
        className={`w-full sm:w-auto py-3 px-6 rounded-lg font-medium flex items-center justify-center ${
          selectedSeats.length === 0
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow transition-all"
        }`}
      >
        Continue to Booking
        <ArrowRight className="ml-2" size={18} />
      </button>
    ) : (
      <div className="flex gap-3 w-full sm:w-auto">
        <button
          onClick={() => setActiveTab("food")}
          className="py-3 px-5 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={completeBooking}
          disabled={selectedSeats.length === 0 || loading || !customerName || !customerPhone}
          className={`py-3 px-6 rounded-lg font-medium flex items-center justify-center ${
            selectedSeats.length === 0 || loading || !customerName || !customerPhone
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow transition-all"
          }`}
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Sending to WhatsApp...
            </>
          ) : (
            <>
              <MessageCircle className="mr-2" size={18} />
              Book via WhatsApp
            </>
          )}
        </button>
      </div>
    )}
  </div>
</div>
    </div>
  );
};

function loadScript(src) {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default SeatSelection;
